import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withTranslation } from "react-i18next";
import { Form, Input, Button, Select, message, Switch, Card, Row, Col, Upload, Tabs } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import queryString from 'query-string';

import * as types from 'reducer/actionTypes';
import { isEmpty } from 'script/common';
import { jsonToCascader } from 'script/uniAdapter';

const ExecuteRequest = ({ t, interfaceType, webofficeURLParam, adapterName, tabName }) => {
	const [selectedInterfaceType, setselectedInterfaceType] = useState('');
	const test = useSelector(state => state.uniAdapter?.[interfaceType]);
	const url = useSelector(state => state.uniAdapter?.[interfaceType]?.request?.url || '');
	const param = useSelector(state => state.uniAdapter?.[interfaceType]?.request?.param || []);
	const method = useSelector(state => state.uniAdapter?.[interfaceType]?.request?.method || 'GET');
	const header = useSelector(state => state.uniAdapter?.[interfaceType]?.request?.header || null);
	const body = useSelector(state => state.uniAdapter?.[interfaceType]?.request?.body || null);
	const cookie = useSelector(state => state.uniAdapter?.[interfaceType]?.request?.cookie || null);
	const isRequestActive = useSelector(state => state.uniAdapter?.[interfaceType]?.isRequestActive);
	const executeResponse = useSelector(state => state.uniAdapter?.[interfaceType]?.response);
	const uniAdapterParam = useSelector(state => state.uniAdapter?.adapterParam);
	/** ========================================================================================================================================== **/
	const [ form ] = Form.useForm();
	const [fileList, setFileList] = useState([]); // State for storing uploaded files
	/** ========================================================================================================================================== **/
	const dispatch = useDispatch();
	const fetchGetUniAdapterInterface = (value) => {
		dispatch({ type: types.FETCH_GET_UNIADAPTER_INTERFACE_REQUEST, value: value })
	};
	
	const fetchGetUniAdapterHttpExecute = (value, uploadFile) => {
		dispatch({ type: types.FETCH_GET_UNIADAPTER_HTTP_EXECUTE_REQUEST, interfaceType: interfaceType, value: value, uploadFile: uploadFile })
	};

	const setInterfacerResponseSource = (value) => {
		dispatch({type: types.SET_INTERFACE_RESPONSE_SOURCE, interfaceType:interfaceType, responseSource: value})
	};

	const setIsRequestActive = (value) => {
		dispatch({type: types.SET_INTERFACE_IS_REQUEST_ACTIVE, interfaceType:interfaceType, isRequestActive: value})
	};
	/** ========================================================================================================================================== **/
		
	// Get UniAdapter interface and adapterParam information
	useEffect(() => {
		fetchGetUniAdapterInterface({ interfaceType: interfaceType, adapterName: adapterName })
	  }, []);

	// Update resource real-time 
	useEffect(() => {
		const resourceData = () => {
			let data = [];
			
			const _response = data.push(jsonToCascader({ response: { body: executeResponse?.body, header: executeResponse?.header, cookie: executeResponse?.cookie }}));
			data.push(_response)

			if (interfaceType === 'start') {
				data.push(jsonToCascader({ adapter: { param: {...webofficeURLParam} }}));
			} else {
				data.push(jsonToCascader({ adapter: { param: {...uniAdapterParam, ...webofficeURLParam} }}));
			}
			
			return data;
		};

		setInterfacerResponseSource(resourceData());
		
	  }, [webofficeURLParam, uniAdapterParam, executeResponse])

	// Update form fields
	useEffect(() => {
		form.setFieldsValue({ 
			url,
			param, 
			method, 
			header: isEmpty(header) ? null : JSON.stringify(header, null, 2),
			body: isEmpty(body) ? null : JSON.stringify(body, null, 2),
		});
		handleUrlChange(url)
	}, [url, param, method, header, body, form]);

	// Update responseSource data
	useEffect(() => {
		if (executeResponse) {
			const { error, status, body, header, cookie } = executeResponse;

			if (error) {
				message.error(`Error: ${message}`);
			} else if (status) {
				message.success('Request successful!');
			}
		}
	}, [executeResponse]);

	// URL -> URLSearchParams
	const handleUrlChange = (urlValue) => {		
		if (!isEmpty(urlValue)) {
			// URLSearchParams (URL?key:value)
			const searchParams = new URLSearchParams(urlValue.split('?')[1]);
			let newParams = [];
			for (let [key, value] of searchParams.entries()) {
				newParams.push({ key, value });
			}	
			form.setFieldsValue({ param: newParams });
		}
	};

	// URLSearchParams -> URL
	const handleParamChange = () => {
		const currentParams = form.getFieldValue('param') || [];
		let queryparam = currentParams.map((data) => {
			let field = data.key ? data.key : '';
			let value = data.value ? data.value : '';
			return (field || value)? [field, value] : null;
		})
		.filter(Boolean);

		queryparam = new URLSearchParams(queryparam).toString();
		const newUrl = isEmpty(queryparam) ? `${queryString.parseUrl(url).url}`: `${queryString.parseUrl(url).url}?${queryparam}`;

		// Update URL
		form.setFieldsValue({ url: newUrl });
	};
	
	// Send Request
	const handleRequest = () => {
		try {
			const formValue = form.getFieldValue();
			let value = {
				interfaceType: interfaceType,
				url: formValue.url,
				method: formValue.method,
				header: isEmpty(formValue.header)? null : JSON.parse(formValue.header),
				body: isEmpty(formValue.body)? null : JSON.parse(formValue.body),
				adapterName: adapterName,
			}

			let uploadFile = null;
			if (interfaceType === 'put') {
				// `get upload file`
				uploadFile = (fileList.length > 0) ? fileList[0].originFileObj : null;
				if (uploadFile == null) {
					message.error("'Upload File' is empty");
					return;
				}
			}

			fetchGetUniAdapterHttpExecute(value, uploadFile);

		} catch (e) {
			message.error(e.message);
		}
	}

	// Handle file upload change
	const handleFileChange = ({ fileList }) => {
		setFileList(fileList);
	};

	// URL Parameter UI
	const renderParams = () => {
		return (
			<Form.List name="param">
				{(fields, { add, remove }) => (
				<>
					{fields.map(({ key, name, ...restField }, index) => (
					<Row gutter={8} key={key} align="middle">
						<Col span={11}>
							<Form.Item
								{...restField}
								name={[name, 'key']}
								rules={[{ required: true, message: 'Key is required' }]}
							>
								<Input placeholder="Key" onChange={(e) => handleParamChange()}/>
							</Form.Item>
						</Col>
						<Col span={11}>
							<Form.Item
								{...restField}
								name={[name, 'value']}
								rules={[{ required: true, message: 'Value is required' }]}
							>
								<Input placeholder="Value" onChange={(e) => handleParamChange()}/>
							</Form.Item>
						</Col>
						<Col span={2} style={{ textAlign: 'right' }}>
							<Button 
								type="link" 
								onClick={() => {
									remove(name)
									handleParamChange();
								}}
								style={{ padding: 0, color: 'red' }}
							>
								x
							</Button>
						</Col>
					</Row>
					))}
		
					{/* 파라미터 추가 버튼 */}
					<Button type="dashed" onClick={() => add({key:'', value:''})} style={{ width: '100%', marginTop: '16px' }}> + Add Param </Button>
				</>
				)}
			</Form.List>
		);
	};

	return (
		<>
			<Card
				type="inner"
				title="Request"
				style={{ marginTop: 20 }}
				extra={<Switch unCheckedChildren="미사용" checkedChildren="사용" checked={isRequestActive} onChange={(e) => setIsRequestActive(e)} />}
			>
				{isRequestActive && (
					<Form
						form={form}
						layout="vertical"
					>
						<Row gutter={8} align="middle">
							<Col span={4}>
								{/* Method */}
								<Form.Item
									name="method"
									label="Method"
								>
									<Select>
										<Select.Option value="GET">GET</Select.Option>
										<Select.Option value="POST">POST</Select.Option>
										<Select.Option value="PATCH">PATCH</Select.Option>
										<Select.Option value="PUT">PUT</Select.Option>
										<Select.Option value="DELETE">DELETE</Select.Option>
										<Select.Option value="HEAD">HEAD</Select.Option>
									</Select>
								</Form.Item>
							</Col>
							
							<Col span={20}>
								{/* Url */}
								<Form.Item
									name="url"
									label="URL"
									// rules={[{ required: true, message: 'Please enter the API URL' }]}
								>
									<Input
										placeholder="Enter API URL"
										onChange={(e) => handleUrlChange(e.target.value)}
									/>
								</Form.Item>
							</Col>
						</Row>

						<Tabs
							defaultActiveKey = '2'
							items={
								[
									{
										key: '1',
										label: 'Parameters',
										children: 
											<Form.Item>
												{renderParams()}
											</Form.Item>
									},
									{
										key: '2',
										label: 'Header (JSON)',
										children: 
											<Form.Item name="header">
												<Input.TextArea placeholder='{"Content-Type": "application/json"}' rows={4}/>
											</Form.Item>
									},
									{
										key: '3',
										label: (interfaceType === 'put'? 'Upload File' : 'Body (JSON)') ,
										children: 
											(interfaceType === 'put' ? (
												<Form.Item>
													<Upload
														beforeUpload={() => false}
														fileList={fileList}
														onChange={handleFileChange}
														maxCount={1}
													>
														<Button icon={<UploadOutlined />}>Select File</Button>
													</Upload>
												</Form.Item>
											) : (
												<Form.Item name="body">
													<Input.TextArea placeholder='{"key": "value"}' rows={4}/>
												</Form.Item>
											))
									}
								]
							}
						/>					

						<Form.Item>
							<Button type="default" onClick={handleRequest} style={{ float: 'right' }}>
								Send Request
							</Button>
						</Form.Item>
					</Form>
				)} 
			</Card>
		</>
	);
}
	
export default ExecuteRequest;